/**
 * From the declared settings and one scope's layer to the rows of the kit's
 * settings list (I-2510): per field the value the layer shows — its own,
 * else what a layer before it gives, else the declared default — where it
 * comes from, whether a layer before it locked it, and the lock the layer
 * itself may set. Pure functions: `ConnectSettingsList` feeds them reactive
 * views, tests feed them plain ones.
 *
 * A `distinctGroup` drops, from a `choice`, a `select` or a `keybinding`,
 * every value another field of the same key and group shows on this scope. The
 * option of no value (`null` or `""`) and the row's own shown value stay. A
 * keybinding row also refuses a recorded combo that another field of its
 * group holds — compared as modifier set and key, equal on either platform
 * (`Mod` once as ⌘, once as Ctrl), so `Mod+Shift+F`, `shift+mod+f` and
 * `Ctrl+Shift+F` are one — and one its declaration refuses
 * (`keybindingProblem`). ⌘K and Ctrl+K — K with Ctrl, Meta or Mod, whatever
 * else is held — are refused for every keybinding, whatever the declaration
 * says: the global search stays.
 *
 * An `order` becomes a kit row with its options and the normalized order
 * (`connectSettingsOrder`) joined by `,` as its value; the kit emits the
 * joined string again, which is what the field stores.
 */
import type {
  NxSettingsListItem,
  NxSettingsListOption,
  NxSettingsListOrigin,
  NxSettingsListSection,
} from "@blpde/nx-ui";
import {
  accountConfigLayerSource,
  type AccountConfigLayerRef,
  type AccountConfigLayerView,
} from "../config/layerView";
import {
  connectComboIdentities,
  connectComboIsGlobalSearch,
} from "../config/keybindingCombo";
import {
  CONNECT_INSTALLATION_LAYER,
  connectSettingsOrder,
  connectSettingsShownValue,
  connectSettingsValueAt,
} from "../config/settingsDeclaration";
import type {
  ConnectSettingsFieldView,
  ConnectSettingsSectionView,
} from "./catalog";
import type {
  ConnectSettingsAppearanceOption,
  ConnectSettingsScope,
} from "./scope";

/** What a row needs to know about the scope it shows. */
export interface ConnectSettingsItemContext {
  /** The scope's layer of a key; null while it is not read. */
  view: (key: string) => AccountConfigLayerView | null;
  /** The scope may lock top-level fields (tenant, company). */
  locking: boolean;
  /** Nothing can be changed. */
  readOnly: boolean;
  /** The choices of `appearance` fields. */
  appearanceOptions: readonly ConnectSettingsAppearanceOption[];
  /** A layer's caption as the source of a value or of a lock. */
  caption: (layer: AccountConfigLayerRef) => string;
  /** „Schon vergeben: <title>“ — the reason a keybinding refuses a combo another row holds. */
  keybindingTaken?: (field: string) => string;
  /** „⌘K bzw. Strg+K bleibt die globale Suche.“ — the reason a keybinding refuses ⌘K or Ctrl+K. */
  keybindingGlobalSearch?: string;
}

/** Shown when the host passes no translated text. */
const GLOBAL_SEARCH_FALLBACK = "⌘K or Ctrl+K stays the global search.";

/**
 * The fields a scope shows: the admin-only ones on an administrator's layer
 * only, the installation-only ones on the installation's layer only, none of
 * a key the layer cannot hold; sections left without a field drop out.
 */
export function connectSettingsScopeSections(
  sections: readonly ConnectSettingsSectionView[],
  scope:
    | (Pick<ConnectSettingsScope, "adminLayer" | "excludedKeys"> &
        Partial<Pick<ConnectSettingsScope, "target">>)
    | undefined,
): ConnectSettingsSectionView[] {
  const excluded = new Set(scope?.excludedKeys ?? []);
  const installation =
    scope?.target?.subjectType === CONNECT_INSTALLATION_LAYER.subjectType &&
    scope.target.subjectId === CONNECT_INSTALLATION_LAYER.subjectId;
  return sections
    .map((section) => ({
      ...section,
      fields: section.fields.filter(
        (field) =>
          !excluded.has(field.key) &&
          (!field.adminOnly || scope?.adminLayer) &&
          (!field.installationOnly || installation),
      ),
    }))
    .filter((section) => section.fields.length > 0);
}

/** What a row shows on this scope, and where that value comes from. */
interface ScopeShown {
  value: unknown;
  origin: NxSettingsListOrigin;
  /** The layer a locked or inherited value comes from. Unset for the platform and the default. */
  source?: AccountConfigLayerRef;
  /** The key is not read yet: the row shows the default and cannot be edited. */
  unread: boolean;
  /** An earlier layer locks the field, so the row offers no lock of its own. */
  locked: boolean;
}

/**
 * The value a row shows: a lock keeps what the layers before this one give
 * (or the declared default when they give nothing — the merge drops the
 * layer's own value), else the layer's own value, else the inherited one,
 * else the declared default.
 */
function shownOnScope(
  field: Pick<
    ConnectSettingsFieldView,
    "key" | "path" | "control" | "values" | "default"
  >,
  ctx: ConnectSettingsItemContext,
): ScopeShown {
  const declared = connectSettingsShownValue(field, field.default);
  const view = ctx.view(field.key);
  if (!view) {
    return { value: declared, origin: "default", unread: true, locked: false };
  }
  const own = connectSettingsValueAt(view.own, field.path);
  const inherited = connectSettingsValueAt(view.inherited, field.path);
  const lockedBy = view.lockedBy[field.path[0] ?? ""];
  if (lockedBy) {
    const stored = inherited === undefined ? field.default : inherited;
    return {
      value: connectSettingsShownValue(field, stored),
      origin: "locked",
      source: lockedBy,
      unread: false,
      locked: true,
    };
  }
  if (own !== undefined) {
    return {
      value: connectSettingsShownValue(field, own),
      origin: "own",
      unread: false,
      locked: false,
    };
  }
  if (inherited !== undefined) {
    const source = accountConfigLayerSource(view, field.control === "drive-folder" ? [...field.path, "documentId"] : field.path);
    const fromPlatform = !source || source.subjectType === "platform";
    return {
      value: connectSettingsShownValue(field, inherited),
      origin: fromPlatform ? "default" : "inherited",
      ...(fromPlatform || !source ? {} : { source }),
      unread: false,
      locked: false,
    };
  }
  return { value: declared, origin: "default", unread: false, locked: false };
}

/**
 * What a field's shown value counts as in its `distinctGroup`: a letter or
 * number as is, a keybinding as its identities (equal on either platform).
 */
function distinctTokens(
  field: Pick<ConnectSettingsFieldView, "control">,
  value: unknown,
): (string | number)[] {
  if (field.control === "keybinding") return connectComboIdentities(value);
  if (typeof value === "string" && value !== "") return [value];
  if (typeof value === "number") return [value];
  return [];
}

function inDistinctGroup(
  field: ConnectSettingsFieldView,
  scopeFields: readonly ConnectSettingsFieldView[],
): ConnectSettingsFieldView[] {
  const group = field.distinctGroup;
  if (!group) return [];
  return scopeFields.filter(
    (other) =>
      other.id !== field.id &&
      other.key === field.key &&
      other.distinctGroup === group,
  );
}

/** Values the other fields of this key and `distinctGroup` show on this scope. */
function distinctShownValues(
  field: ConnectSettingsFieldView,
  ctx: ConnectSettingsItemContext,
  scopeFields: readonly ConnectSettingsFieldView[],
): Set<string | number> {
  const taken = new Set<string | number>();
  if (
    field.control !== "choice" &&
    field.control !== "select" &&
    field.control !== "keybinding"
  )
    return taken;
  for (const other of inDistinctGroup(field, scopeFields)) {
    for (const token of distinctTokens(field, shownOnScope(other, ctx).value)) {
      taken.add(token);
    }
  }
  return taken;
}

/**
 * A keybinding row's check of a recorded combo: what its declaration refuses,
 * else „Schon vergeben“ when another field of its group shows that combo.
 * The row also asks it about its stored combo. When two fields show the same
 * combo, the first one in declaration order keeps it — the readers bind it
 * there (B-DMS: the activity, the search: the search) — so for the row's own
 * combo only the fields before it count.
 */
function keybindingValidator(
  field: ConnectSettingsFieldView,
  ctx: ConnectSettingsItemContext,
  scopeFields: readonly ConnectSettingsFieldView[],
): (combo: string) => string | null {
  const own = distinctTokens(field, shownOnScope(field, ctx).value);
  const position = scopeFields.findIndex((other) => other.id === field.id);
  return (combo) => {
    // The global search belongs to the shell: no declaration can free it.
    if (connectComboIsGlobalSearch(combo)) {
      return ctx.keybindingGlobalSearch ?? GLOBAL_SEARCH_FALLBACK;
    }
    const problem = field.keybindingProblem?.(combo);
    if (problem) return problem;
    const identities = connectComboIdentities(combo);
    if (!identities.length) return null;
    const isOwn = identities.some((identity) => own.includes(identity));
    for (const other of inDistinctGroup(field, scopeFields)) {
      if (isOwn && scopeFields.indexOf(other) > position) continue;
      const held = distinctTokens(field, shownOnScope(other, ctx).value);
      if (!identities.some((identity) => held.includes(identity))) continue;
      return ctx.keybindingTaken
        ? ctx.keybindingTaken(other.label)
        : `Schon vergeben: ${other.label}`;
    }
    return null;
  };
}

/** `null` and `""` are no value. The row's own shown value stays, so a conflict across layers still displays. */
function optionStays(
  field: Pick<ConnectSettingsFieldView, "control">,
  value: string | number | null,
  ownShown: unknown,
  taken: ReadonlySet<string | number>,
): boolean {
  if (value === null || value === "") return true;
  if (value === ownShown) return true;
  return !distinctTokens(field, value).some((token) => taken.has(token));
}

function optionsOf(
  field: ConnectSettingsFieldView,
  ctx: ConnectSettingsItemContext,
  scopeFields: readonly ConnectSettingsFieldView[],
  ownShown: unknown,
): { control: NxSettingsListItem["control"]; options?: NxSettingsListOption[] } {
  if (field.control === "drive-folder") return { control: "custom" };
  if (field.control === "appearance") {
    return {
      control: "select",
      options: ctx.appearanceOptions.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    };
  }
  if (
    field.control === "choice" ||
    field.control === "select" ||
    field.control === "keybinding"
  ) {
    const taken = distinctShownValues(field, ctx, scopeFields);
    return {
      control: field.control,
      options: field.options
        .filter((option) => optionStays(field, option.value, ownShown, taken))
        .map((option) => ({ ...option })),
    };
  }
  if (field.control === "order") {
    return {
      control: "order",
      options: field.options.map((option) => ({ ...option })),
    };
  }
  return { control: field.control };
}

/** What a row shows: an `order` as its normalized, joined values; every other control as it is. */
function rowValue(field: ConnectSettingsFieldView, shown: unknown): unknown {
  if (field.control !== "order") return shown;
  const values = field.options.flatMap((option) =>
    typeof option.value === "string" ? [option.value] : [],
  );
  return connectSettingsOrder(shown, values).join(",");
}

/** One row: the value of the scope's layer, its origin and the lock. */
export function connectSettingsListItem(
  field: ConnectSettingsFieldView,
  ctx: ConnectSettingsItemContext,
  lockAlso: readonly string[] = [],
  scopeFields: readonly ConnectSettingsFieldView[] = [],
): NxSettingsListItem {
  const view = ctx.view(field.key);
  const lockField = field.path[0] ?? "";
  const shown = shownOnScope(field, ctx);
  const base: NxSettingsListItem = {
    id: field.id,
    label: field.label,
    description: field.description,
    keywords: field.keywords,
    ...optionsOf(field, ctx, scopeFields, shown.value),
    ...(field.control === "keybinding"
      ? { validate: keybindingValidator(field, ctx, scopeFields) }
      : {}),
    ...(field.min !== undefined ? { min: field.min } : {}),
    ...(field.max !== undefined ? { max: field.max } : {}),
    ...(field.step !== undefined ? { step: field.step } : {}),
    ...(field.unit ? { unit: field.unit } : {}),
    ...(field.maxLength ? { maxLength: field.maxLength } : {}),
    value: rowValue(field, shown.value),
    origin: shown.origin,
    ...(shown.source ? { source: ctx.caption(shown.source) } : {}),
    advanced: field.advanced,
    ...(field.group ? { group: field.group } : {}),
    ...(ctx.readOnly ? { disabled: true } : {}),
  };
  // Not read yet: the default, until the layer is known.
  if (shown.unread || !view) return { ...base, disabled: true };
  // A lock leaves the row read-only; this layer does not lock it itself.
  if (shown.locked) return base;

  const lock = ctx.locking && field.lockable
    ? {
        lockable: true,
        locked: view.locks.includes(lockField),
        ...(lockAlso.length ? { lockAlso } : {}),
      }
    : {};
  return { ...base, ...lock };
}

/** The kit's sections for a scope: one row per field, the lock's other fields named. */
export function connectSettingsListSections(
  sections: readonly ConnectSettingsSectionView[],
  ctx: ConnectSettingsItemContext,
): NxSettingsListSection[] {
  const byLock = new Map<string, string[]>();
  const scopeFields = sections.flatMap((section) => section.fields);
  for (const section of sections) {
    for (const field of section.fields) {
      if (!field.lockable) continue;
      const unit = `${field.key}:${field.path[0] ?? ""}`;
      byLock.set(unit, [...(byLock.get(unit) ?? []), field.label]);
    }
  }
  return sections.map((section) => ({
    id: section.id,
    label: section.label,
    ...(section.description ? { description: section.description } : {}),
    groups: section.groups,
    items: section.fields.map((field) => {
      const unit = `${field.key}:${field.path[0] ?? ""}`;
      const others = (byLock.get(unit) ?? []).filter(
        (label) => label !== field.label,
      );
      return connectSettingsListItem(field, ctx, others, scopeFields);
    }),
  }));
}
